'use client'

import classNames from 'clsx'
import { useTranslations } from 'next-intl'
import { useState } from 'react'

import { URLS } from '@/configs/urls'
import { VerifiedMark } from '@/features/clubs/components/VerifiedMark'
import S from '@/features/leaderboard/components/LeaderboardRow.module.scss'
import {
  CountryFlag,
  RankCell,
  RowGlow,
  rowTone,
  SportIcon,
  toneRowClass,
  useCountry
} from '@/features/leaderboard/components/RowParts'
import { formatLeaderboardNumber } from '@/features/leaderboard/formatLeaderboardNumber'
import { Link } from '@/i18n/routing'
import Image from '@/ui/MediaImage'
import { shouldUnoptimizeImageSrc } from '@/utils/imageUrl'

import type { AthleteLeaderboardRow } from '../types'

const FALLBACK_PHOTO = '/assets/default-avatar.svg'

/**
 * One athlete (US-ATH-18.1): place, photo, Display Name, sport, country,
 * Fans, Arena Points, Total Views; the top three glow as the clubs' do; the
 * name opens the athlete's profile.
 */
export function AthleteRow({ row }: { row: AthleteLeaderboardRow }) {
  const t = useTranslations('leaderboardBoard')
  const [expanded, setExpanded] = useState(false)
  const country = useCountry(row.country)
  const tone = rowTone(row.rank, false)
  const src = row.photoUrl || FALLBACK_PHOTO

  return (
    <div className={classNames(S.row, toneRowClass[tone])}>
      <RowGlow tone={tone} />
      <div className={S.summary}>
        <button
          type="button"
          className={S.expandHit}
          aria-expanded={expanded}
          onClick={() => setExpanded(value => !value)}
        >
          <span className={S.srOnly}>{t('toggleDetails')}</span>
        </button>

        <RankCell rank={row.rank} tone={tone} />

        <div className={S.colMain}>
          <Image
            src={src}
            alt={row.displayName}
            width={36}
            height={36}
            className={classNames(S.crest, S.athletePhoto)}
            unoptimized={shouldUnoptimizeImageSrc(src)}
          />
          <div className={classNames(S.identity, S.identityClub)}>
            <div className={S.nameLine}>
              <Link
                href={URLS.CLUB_DETAIL(row.athleteId)}
                className={classNames(S.name, S.nameClub, S.nameLink)}
              >
                {row.displayName}
              </Link>
              {row.isVerified ? (
                <VerifiedMark size={16} className={S.verifiedMark} />
              ) : null}
            </div>
            <div className={S.meta}>
              <CountryFlag code={country.code} />
              {country.name ? (
                <span className={S.desktopOnly}>{country.name}</span>
              ) : null}
              {country.name && row.sportName ? (
                <span className={classNames(S.metaDot, S.desktopOnly)}>·</span>
              ) : null}
              <SportIcon src={row.sportIconUrl} />
              {row.sportName ? <span>{row.sportName}</span> : null}
            </div>
          </div>
        </div>

        <div className={classNames(S.colWide, S.desktopOnly)}>
          <span className={classNames(S.value, S.valueMain)}>
            {formatLeaderboardNumber(row.totalViews)}
          </span>
        </div>
        <div className={classNames(S.colWide, S.desktopOnly)}>
          <span className={S.value}>
            {formatLeaderboardNumber(row.arenaPoints)}
          </span>
        </div>
        <div className={classNames(S.colFans, S.desktopOnly)}>
          <span className={S.value}>{formatLeaderboardNumber(row.fans)}</span>
        </div>

        <div className={classNames(S.mobileViews, S.mobileOnly)}>
          <span className={classNames(S.value, S.valueMain)}>
            {formatLeaderboardNumber(row.totalViews)}
          </span>
          <span className={S.mobileViewsLabel}>{t('columns.views')}</span>
        </div>
      </div>

      {expanded ? (
        <div className={S.details}>
          <div className={S.detailsStats}>
            <div className={S.detailsStat}>
              <span className={S.detailsStatLabel}>
                {t('columns.arenaPoints')}
              </span>
              <span className={S.detailsStatValue}>
                {formatLeaderboardNumber(row.arenaPoints)}
              </span>
            </div>
            <div className={S.detailsStat}>
              <span className={S.detailsStatLabel}>{t('columns.fans')}</span>
              <span className={S.detailsStatValue}>
                {formatLeaderboardNumber(row.fans)}
              </span>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

export function AthleteRowHeader() {
  const t = useTranslations('leaderboardBoard')
  return (
    <>
      <div className={classNames(S.colRank, S.headerCell)}>
        {t('columns.position')}
      </div>
      <div className={classNames(S.colMain, S.headerCell)}>
        {t('columns.athlete')}
      </div>
      <div className={classNames(S.colWide, S.headerCell, S.headerMain)}>
        {t('columns.views')}
      </div>
      <div className={classNames(S.colWide, S.headerCell)}>
        {t('columns.arenaPoints')}
      </div>
      <div className={classNames(S.colFans, S.headerCell)}>
        {t('columns.fans')}
      </div>
    </>
  )
}
